import { loadingManager } from './loading-manager.js'
import { LoadingType } from './types.js'

/**
 * Start a tracked loading operation. Returns a handle with an id and a
 * finish() function. The caller owns the promise lifecycle — they call
 * finish() when done. This is the escape hatch for ad-hoc fetches that
 * don't fit the createQuery/createMutation pattern.
 *
 * @param {string} source - operation source identifier
 * @param {{ type?: import('./types.js').LoadingType, message?: string }} [options]
 * @returns {{ id: string, finish: () => void }}
 */
export function startLoad(source, { type = LoadingType.Query, message } = {}) {
  const id = loadingManager.start(type, { source, message })
  return {
    id,
    finish: () => loadingManager.finish(id),
  }
}

/**
 * Wrap a promise with automatic loading tracking. The loading entry starts
 * immediately and finishes when the promise settles (resolve or reject).
 * The caller handles errors via normal .catch() — the loading tracker
 * does NOT intercept or transform errors.
 *
 * @param {string} source - operation source identifier
 * @param {Promise<any>} promise - the promise to track
 * @returns {Promise<any>} the original promise (transparent pass-through)
 */
export function trackLoad(source, promise) {
  const { finish } = startLoad(source)
  promise.finally(finish)
  return promise
}

/**
 * Wrap an async function with automatic loading tracking. The loading entry
 * starts immediately and finishes when the returned promise settles.
 * Prefer this over `startLoad` for async/await call sites.
 *
 * @param {string} source - operation source identifier
 * @param {() => Promise<any>} fn - async function to track
 * @param {{ type?: import('./types.js').LoadingType, message?: string }} [options]
 * @returns {Promise<any>} the result of fn() (transparent pass-through)
 */
export async function trackAsync(source, fn, { type = LoadingType.Query, message } = {}) {
  const id = loadingManager.start(type, { source, message })
  try {
    return await fn()
  } finally {
    loadingManager.finish(id)
  }
}
